/** Mirrors EPN.TaxCompliance.Api's simulated tax payment contracts (EPN-48). Everything here is simulation only. */
export type TaxPaymentState =
  | 'Draft' | 'PendingApproval' | 'Approved' | 'SimulatedSubmitted' | 'SimulatedSettled'
  | 'Failed' | 'Returned' | 'Cancelled' | 'ReconciliationRequired';

export interface TaxPaymentAttempt {
  id: string;
  attemptNumber: number;
  isActive: boolean;
  state: TaxPaymentState;
  simulatedReference: string | null;
  failureKind: string | null;
  failureCode: string | null;
  allowedNextStates: TaxPaymentState[];
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface TaxPaymentTransition {
  attemptId: string;
  fromState: TaxPaymentState | null;
  toState: TaxPaymentState;
  trigger: string;
  occurredAtUtc: string;
}

export interface TaxPaymentDetail {
  id: string;
  formCode: string;
  periodEndDate: string;
  depositDueDate: string;
  requestedEffectiveDate: string | null;
  depositRuleKey: string | null;
  amount: number;
  simulationNote: string;
  inquiryNote: string | null;
  activeAttempt: TaxPaymentAttempt;
  attempts: TaxPaymentAttempt[];
  history: TaxPaymentTransition[];
  createdAtUtc: string;
}

/** The actions the server offers on a payment. Each one is checked again on the server (permission, recent MFA, separation of duties). */
export type TaxPaymentAction = 'request-approval' | 'approve' | 'submit-simulated' | 'cancel' | 'reissue';

/** The dashboard groups the nine simulated states into the design's four groups. */
export type StatusGroup = 'Pending' | 'Processing' | 'Paid' | 'NeedsAttention' | 'Cancelled';

export const stateGroup: Record<TaxPaymentState, StatusGroup> = {
  Draft: 'Pending',
  PendingApproval: 'Pending',
  Approved: 'Pending',
  SimulatedSubmitted: 'Processing',
  SimulatedSettled: 'Paid',
  Failed: 'NeedsAttention',
  Returned: 'NeedsAttention',
  ReconciliationRequired: 'NeedsAttention',
  Cancelled: 'Cancelled',
};

export const stateLabels: Record<TaxPaymentState, string> = {
  Draft: 'Draft',
  PendingApproval: 'Waiting for approval',
  Approved: 'Approved, not yet submitted',
  SimulatedSubmitted: 'Submitted (simulated)',
  SimulatedSettled: 'Settled (simulated)',
  Failed: 'Failed (file rejected)',
  Returned: 'Returned',
  Cancelled: 'Cancelled',
  ReconciliationRequired: 'Needs reconciliation',
};

export const formLabels: Record<string, string> = { Form941: 'Form 941', Form940: 'Form 940' };

export const actionLabels: Record<TaxPaymentAction, string> = {
  'request-approval': 'Request approval',
  approve: 'Approve',
  'submit-simulated': 'Submit (simulated)',
  cancel: 'Cancel payment',
  reissue: 'Issue again',
};
